// src/context/UserContext.tsx
"use client";
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User } from '@prisma/client';
import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  createUser as createUserService,
  getUser as getUserService,
  updateUser as updateUserService,
  deleteUser as deleteUserService,
  getUsers as getUsersService,
} from '../services/auth';
import { useAuth } from './AuthContext';

interface UserContextType {
  users: User[];
  fetchUsers: (
    page: number,
    limit: number,
    searchTerm: string,
    sortBy: string
  ) => Promise<void>;
  createUser: (user: Partial<User>) => Promise<void>;
  getUser: (id: number) => Promise<User | null>;
  updateUser: (id: number, user: Partial<User>) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
  currentPage: number;
  hasNextPage: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const userContext = useProvideUser();
  return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

const useProvideUser = (): UserContextType => {
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const handleError = useCallback((error: unknown, defaultMessage: string) => {
    let message = defaultMessage;

    if (error instanceof AxiosError) {
      message = error.response?.data?.message || defaultMessage;
    } else if (error instanceof Error) {
      message = error.message;
    }

    console.error('User Error:', message);
    toast.error(message);
  }, []);

  const fetchUsers = useCallback(
    async (page: number, limit: number, searchTerm: string, sortBy: string) => {
      try {
        const response = await getUsersService(page, limit, searchTerm, sortBy);
        setUsers(response.users || []);
        setHasNextPage(response.users.length === limit);
        setCurrentPage(page);
      } catch (error: unknown) {
        handleError(error, 'Failed to fetch users.');
      }
    },
    [handleError]
  );

  const createUser = useCallback(
    async (user: Partial<User>) => {
      try {
        const response = await createUserService(user);
        toast.success('User created successfully.');
        // Optionally, refetch users or update state
      } catch (error: unknown) {
        handleError(error, 'Failed to create user.');
      }
    },
    [handleError]
  );

  const getUser = useCallback(
    async (id: number): Promise<User | null> => {
      try {
        const response = await getUserService(id);
        return response.user || null;
      } catch (error: unknown) {
        handleError(error, 'Failed to fetch user.');
        return null;
      }
    },
    [handleError]
  );

  const updateUser = useCallback(
    async (id: number, user: Partial<User>) => {
      try {
        await updateUserService(id, user);
        toast.success('User updated successfully.');
        // Optionally, refetch users or update state
      } catch (error: unknown) {
        handleError(error, 'Failed to update user.');
      }
    },
    [handleError]
  );

  const deleteUser = useCallback(
    async (id: number) => {
      try {
        await deleteUserService(id);
        toast.success('User deleted successfully.');
        // Optionally, refetch users or update state
      } catch (error: unknown) {
        handleError(error, 'Failed to delete user.');
      }
    },
    [handleError]
  );

  return {
    users,
    fetchUsers,
    createUser,
    getUser,
    updateUser,
    deleteUser,
    currentPage,
    hasNextPage,
  };
};
